
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../apiClient';
import { useAuth } from '../firebase';

const componentTranslations = {
    ja: {
        title: "{{memberName}} の設定",
        memberInfoTitle: "メンバー情報",
        nameHeader: "名前",
        departmentHeader: "部署",
        phoneHeader: "電話番号",
        loading: "メンバー情報を読み込んでいます...",
        memberNotFound: "指定されたメンバーが見つかりません。",
        saveButton: "変更を保存",
        savingButton: "保存中...",
        backButton: "チーム管理に戻る",
        saveSuccess: "情報を正常に更新しました。",
        saveError: "情報の更新に失敗しました。",
        lastAdminError: "最後の管理者の役割を変更することはできません。",
        roleTitle: "役割",
        roleAdmin: "管理者",
        roleMember: "メンバー",
        allPermissionsGranted: "管理者にはすべての権限が付与されます。個別の設定は変更できません。",
        category_invoice: "請求書関連の権限",
        invoice_canUpload: "請求書のアップロード",
        invoice_canViewScheduled: "予約済み請求書の閲覧",
        invoice_canDeleteScheduled: "予約済み請求書の削除",
        invoice_canViewHistory: "請求履歴の閲覧",
        category_customer: "顧客関連の権限",
        customer_canUpload: "顧客データのアップロード",
        customer_canDelete: "顧客データの削除",
        category_settings: "設定関連の権限",
        settings_canViewSubscription: "契約内容の閲覧",
        settings_canChangePlan: "契約プランの変更",
        settings_canViewBillwave: "BillWave設定の閲覧",
        settings_canUpdateBillwave: "BillWave設定の更新",
        category_customer_info: "お客様情報の権限",
        customerInfo_canViewPaymentMethods: "お支払い方法の閲覧",
        customerInfo_canUpdatePaymentMethods: "お支払い方法の更新",
        customerInfo_canViewCustomerInfo: "請求先情報の閲覧",
        customerInfo_canUpdateCustomerInfo: "請求先情報の更新",
        category_billing: "ご利用料金の権限",
        billing_canViewHistory: "ご利用料金の閲覧"
    },
    en: {
        title: "Settings for {{memberName}}",
        memberInfoTitle: "Member Information",
        nameHeader: "Name",
        departmentHeader: "Department",
        phoneHeader: "Phone Number",
        loading: "Loading member information...",
        memberNotFound: "The specified member could not be found.",
        saveButton: "Save Changes",
        savingButton: "Saving...",
        backButton: "Back to Team Management",
        saveSuccess: "Information updated successfully.",
        saveError: "Failed to update information.",
        lastAdminError: "The role of the last administrator cannot be changed.",
        roleTitle: "Role",
        roleAdmin: "Administrator",
        roleMember: "Member",
        allPermissionsGranted: "Administrators are granted all permissions. Individual settings cannot be changed.",
        category_invoice: "Invoice Permissions",
        invoice_canUpload: "Upload Invoices",
        invoice_canViewScheduled: "View Scheduled Invoices",
        invoice_canDeleteScheduled: "Delete Scheduled Invoices",
        invoice_canViewHistory: "View Invoice History",
        category_customer: "Customer Permissions",
        customer_canUpload: "Upload Customer Data",
        customer_canDelete: "Delete Customer Data",
        category_settings: "Settings Permissions",
        settings_canViewSubscription: "View Subscription Details",
        settings_canChangePlan: "Change Subscription Plan",
        settings_canViewBillwave: "View BillWave Settings",
        settings_canUpdateBillwave: "Update BillWave Settings",
        category_customer_info: "Customer Information Permissions",
        customerInfo_canViewPaymentMethods: "View Payment Methods",
        customerInfo_canUpdatePaymentMethods: "Update Payment Methods",
        customerInfo_canViewCustomerInfo: "View Billing Information",
        customerInfo_canUpdateCustomerInfo: "Update Billing Information",
        category_billing: "Billing Permissions",
        billing_canViewHistory: "View Billing Charges"
    }
};

const permissionStructure = [
    { category: "invoice", labelKey: "category_invoice", permissions: [
        { key: "canUpload", labelKey: "invoice_canUpload" },
        { key: "canViewScheduled", labelKey: "invoice_canViewScheduled" },
        { key: "canDeleteScheduled", labelKey: "invoice_canDeleteScheduled" },
        { key: "canViewHistory", labelKey: "invoice_canViewHistory" },
    ]},
    { category: "customer", labelKey: "category_customer", permissions: [
        { key: "canUpload", labelKey: "customer_canUpload" },
        { key: "canDelete", labelKey: "customer_canDelete" },
    ]},
    { category: "settings", labelKey: "category_settings", permissions: [
        { key: "canViewSubscription", labelKey: "settings_canViewSubscription" },
        { key: "canChangePlan", labelKey: "settings_canChangePlan" },
        { key: "canViewBillwave", labelKey: "settings_canViewBillwave" },
        { key: "canUpdateBillwave", labelKey: "settings_canUpdateBillwave" },
    ]},
    { category: "customerInfo", labelKey: "category_customer_info", permissions: [
        { key: "canViewPaymentMethods", labelKey: "customerInfo_canViewPaymentMethods" },
        { key: "canUpdatePaymentMethods", labelKey: "customerInfo_canUpdatePaymentMethods" },
        { key: "canViewCustomerInfo", labelKey: "customerInfo_canViewCustomerInfo" },
        { key: "canUpdateCustomerInfo", labelKey: "customerInfo_canUpdateCustomerInfo" },
    ]},
    { category: "billing", labelKey: "category_billing", permissions: [
        { key: "canViewHistory", labelKey: "billing_canViewHistory" },
    ]}
];

const ToggleSwitchStyles = () => (
    <style>{`
        .toggle-switch {
            position: relative;
            display: inline-block;
            width: 50px;
            height: 28px;
        }
        .toggle-switch input { display: none; }
        .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #ccc;
            transition: .4s;
            border-radius: 28px;
        }
        .slider:before {
            position: absolute;
            content: "";
            height: 20px;
            width: 20px;
            left: 4px;
            bottom: 4px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
        }
        input:checked + .slider {
            background-color: #4F46E5; /* Indigo 600 */
        }
        input:checked + .slider:before {
            transform: translateX(22px);
        }
        input:disabled + .slider {
            cursor: not-allowed;
            background-color: #e0e0e0;
        }
    `}</style>
);

const ToggleSwitch = ({ id, checked, onChange, label, disabled }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        opacity: disabled ? 0.6 : 1,
    }}>
        <span style={{ color: disabled ? '#999' : '#333' }}>{label}</span>
        <label
            htmlFor={id}
            className="toggle-switch"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            onClick={(e) => {
                e.preventDefault();
                if (!disabled) {
                    onChange();
                }
            }}
        >
            <input id={id} type="checkbox" checked={checked} readOnly disabled={disabled} />
            <span className="slider round"></span>
        </label>
    </div>
);

const ManagePermissions = () => {
    const { t, i18n } = useTranslation();
    const { memberId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = useAuth();

    const [member, setMember] = useState(null);
    const [role, setRole] = useState('member');
    const [representativeName, setRepresentativeName] = useState('');
    const [departmentName, setDepartmentName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        Object.entries(componentTranslations).forEach(([lang, resources]) => {
            i18n.addResourceBundle(lang, 'translation', resources, true, true);
        });
    }, [i18n]);

    const initializePermissions = (memberData) => {
        const initialPermissions = {};
        permissionStructure.forEach(cat => {
            initialPermissions[cat.category] = {};
            cat.permissions.forEach(perm => {
                initialPermissions[cat.category][perm.key] = memberData?.permissions?.[cat.category]?.[perm.key] || false;
            });
        });
        return initialPermissions;
    };

    const fetchInitialMemberData = useCallback(async () => {
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await apiClient.get(`/v1/team/members/${memberId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch member data');
            const data = await response.json();
            setMember(data);
            setRole(data.role || 'member');
            setRepresentativeName(data.representativeName || '');
            setDepartmentName(data.departmentName || '');
            setPhoneNumber(data.phoneNumber || '');
            setPermissions(initializePermissions(data));
        } catch (err) {
            setError(t('memberNotFound'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [memberId, currentUser, t]);

    useEffect(() => {
        if (location.state?.member) {
            const memberData = location.state.member;
            setMember(memberData);
            setRole(memberData.role || 'member');
            setRepresentativeName(memberData.representativeName || '');
            setDepartmentName(memberData.departmentName || '');
            setPhoneNumber(memberData.phoneNumber || '');
            setPermissions(initializePermissions(memberData));
            setLoading(false);
        } else {
            fetchInitialMemberData();
        }
    }, [location.state, fetchInitialMemberData]);

    useEffect(() => {
        if (role === 'admin') {
            const allPermissions = {};
            permissionStructure.forEach(cat => {
                allPermissions[cat.category] = {};
                cat.permissions.forEach(perm => {
                    allPermissions[cat.category][perm.key] = true;
                });
            });
            setPermissions(allPermissions);
        }
    }, [role]);

    const handlePermissionChange = (category, key) => {
        setPermissions(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: !prev[category]?.[key],
            },
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError('');
        setSuccess('');

        if (phoneNumber && !phoneNumber.match(/^\d{10,11}$/)) {
            setError('電話番号はハイフンなしの形式で入力してください（例: 09012345678）。');
            setIsSaving(false);
            return;
        }

        try {
            const token = await currentUser.getIdToken();
            const headers = { Authorization: `Bearer ${token}` };

            if (member.role === 'admin' && role === 'member') {
                const teamResponse = await apiClient.get('/v1/team', { headers });
                if (teamResponse.ok) {
                    const { members } = await teamResponse.json();
                    const adminCount = members.filter(m => m.role === 'admin').length;
                    if (adminCount <= 1) {
                        setError(t('lastAdminError'));
                        setIsSaving(false);
                        return;
                    }
                }
            }

            const memberUpdatePayload = {
                role,
                representativeName,
                departmentName,
                phoneNumber,
            };

            const updateMemberPromise = apiClient.put(`/v1/team/members/${memberId}`, memberUpdatePayload, { headers });
            const updatePermissionsPromise = apiClient.post(`/v1/team/members/${memberId}/permissions`, { permissions }, { headers });

            const [memberResult, permissionsResult] = await Promise.all([updateMemberPromise, updatePermissionsPromise]);

            if (!memberResult.ok || !permissionsResult.ok) {
                throw new Error('One or more updates failed');
            }
            
            setSuccess(t('saveSuccess'));

            // Update local state after successful save
            setMember(prevMember => ({
                ...prevMember,
                ...memberUpdatePayload,
                permissions
            }));

        } catch (err) {
            setError(t('saveError'));
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <div className="p-8 text-center">{t('loading')}</div>;
    }

    if (!member) {
        return <div className="p-8 text-center text-red-500">{t('memberNotFound')}</div>;
    }

    const isRoleAdmin = role === 'admin';

    return (
        <>
            <ToggleSwitchStyles />
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-6">
                        <button onClick={() => navigate('/settings/team-management')} className="text-indigo-600 hover:underline">
                            &larr; {t('backButton')}
                        </button>
                    </div>

                    <div className="bg-white p-8 rounded-lg shadow-md">
                        <h1 className="text-3xl font-bold mb-4 text-gray-800">
                            {t('title', { memberName: member.representativeName || member.email })}
                        </h1>
                        
                        {error && <p className="text-red-600 mb-6 p-3 bg-red-100 rounded-md">{error}</p>}
                        {success && <p className="text-green-600 mb-6 p-3 bg-green-100 rounded-md">{success}</p>}

                        <div className="mb-8 p-4 border rounded-md bg-gray-50">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('memberInfoTitle')}</h2>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="representativeName" className="block text-sm font-medium text-gray-700">{t('nameHeader')}</label>
                                    <input type="text" id="representativeName" value={representativeName} onChange={(e) => setRepresentativeName(e.target.value)} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" />
                                </div>
                                <div>
                                    <label htmlFor="departmentName" className="block text-sm font-medium text-gray-700">{t('departmentHeader')}</label>
                                    <input type="text" id="departmentName" value={departmentName} onChange={(e) => setDepartmentName(e.target.value)} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" />
                                </div>
                                <div>
                                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">{t('phoneHeader')}</label>
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder="例: 09012345678"
                                        pattern="\d{10,11}"
                                        title="電話番号はハイフンなしで入力してください。"
                                        className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mb-8 p-4 border rounded-md bg-gray-50">
                            <label htmlFor="role-select" className="block text-lg font-semibold text-gray-800 mb-2">{t('roleTitle')}</label>
                            <select 
                                id="role-select"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className="block w-full max-w-xs p-2 border border-gray-300 rounded-md shadow-sm"
                            >
                                <option value="member">{t('roleMember')}</option>
                                <option value="admin">{t('roleAdmin')}</option>
                            </select>
                            {isRoleAdmin && <p className="text-sm text-gray-600 mt-3">{t('allPermissionsGranted')}</p>}
                        </div>

                        <div className="space-y-6">
                            {permissionStructure.map((category) => (
                                <div key={category.category}>
                                    <h4 className="text-xl font-semibold border-b-2 border-gray-200 pb-2 mb-2 text-gray-700">
                                        {t(category.labelKey)}
                                    </h4>
                                    <div className="pl-2">
                                        {category.permissions.map((perm) => (
                                            <ToggleSwitch
                                                key={perm.key}
                                                id={`${category.category}-${perm.key}`}
                                                label={t(perm.labelKey)}
                                                checked={!!permissions[category.category]?.[perm.key]}
                                                onChange={() => handlePermissionChange(category.category, perm.key)}
                                                disabled={isRoleAdmin}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 text-right">
                            <button
                                onClick={handleSave}
                                className="bg-indigo-600 text-white font-semibold py-3 px-8 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400"
                                disabled={isSaving}
                            >
                                {isSaving ? t('savingButton') : t('saveButton')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ManagePermissions;
